import { useState } from 'react';
import type {
  ParseResult,
  StatusFilter,
  DuplicateAction,
  ParsedFaqRow,
  ParsedSymptomRow,
} from '../types';
import { SummaryCards } from '../components/SummaryCards';
import { FaqPreviewTable } from '../components/FaqPreviewTable';
import { SymptomPreviewTable } from '../components/SymptomPreviewTable';

interface PreviewStepProps {
  fileName: string;
  parseResult: ParseResult;
  onUpdateParseResult: (result: ParseResult) => void;
  onSubmit: () => void;
  onBack: () => void;
  isUploading: boolean;
  uploadError: string | null;
}

export const PreviewStep: React.FC<PreviewStepProps> = ({
  fileName,
  parseResult,
  onUpdateParseResult,
  onSubmit,
  onBack,
  isUploading,
  uploadError,
}) => {
  const [activeTab, setActiveTab] = useState<'faq' | 'symptom'>('faq');
  const [filter, setFilter] = useState<StatusFilter>('all');

  const { faqs, symptoms, errors, newCategories } = parseResult;
  const allRows = [...faqs, ...symptoms];
  const newCount = allRows.filter((r) => r.status === 'new').length;
  const duplicateCount = allRows.filter((r) => r.status === 'duplicate').length;
  const errorCount = errors.length;
  const selectedCount = allRows.filter((r) => r.selected && r.status !== 'error').length;

  const filters: { label: string; value: StatusFilter }[] = [
    { label: 'すべて', value: 'all' },
    { label: '新規のみ', value: 'new' },
    { label: '重複のみ', value: 'duplicate' },
    { label: 'エラーのみ', value: 'error' },
  ];

  const updateFaqs = (updater: (rows: ParsedFaqRow[]) => ParsedFaqRow[]) => {
    onUpdateParseResult({ ...parseResult, faqs: updater(faqs) });
  };

  const updateSymptoms = (updater: (rows: ParsedSymptomRow[]) => ParsedSymptomRow[]) => {
    onUpdateParseResult({ ...parseResult, symptoms: updater(symptoms) });
  };

  return (
    <div>
      <div className="mb-4 flex items-center justify-between rounded-lg bg-gray-50 px-4 py-2.5">
        <span className="text-sm font-medium text-gray-700">{fileName}</span>
        <button onClick={onBack} className="text-xs text-gray-500 hover:text-gray-700">
          ファイル変更
        </button>
      </div>

      <div className="mb-5">
        <SummaryCards
          newCount={newCount}
          duplicateCount={duplicateCount}
          errorCount={errorCount}
          newCategoryCount={newCategories.length}
        />
      </div>

      <div className="mb-3 flex items-center gap-2">
        <span className="text-xs text-gray-500">表示:</span>
        {filters.map((f) => (
          <button
            key={f.value}
            onClick={() => setFilter(f.value)}
            className={`rounded-md px-3 py-1 text-xs font-medium ${filter === f.value ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
          >
            {f.label}
          </button>
        ))}
      </div>

      <div className="flex border-b-2 border-gray-200">
        {faqs.length > 0 && (
          <button
            onClick={() => setActiveTab('faq')}
            className={`px-6 py-2.5 text-sm font-medium ${activeTab === 'faq' ? '-mb-0.5 border-b-2 border-blue-600 text-blue-600' : 'text-gray-500 hover:text-gray-700'}`}
          >
            FAQ ({faqs.length}件)
          </button>
        )}
        {symptoms.length > 0 && (
          <button
            onClick={() => setActiveTab('symptom')}
            className={`px-6 py-2.5 text-sm font-medium ${activeTab === 'symptom' ? '-mb-0.5 border-b-2 border-blue-600 text-blue-600' : 'text-gray-500 hover:text-gray-700'}`}
          >
            症状 ({symptoms.length}件)
          </button>
        )}
      </div>

      <div className="mt-0">
        {activeTab === 'faq' && faqs.length > 0 && (
          <FaqPreviewTable
            rows={faqs}
            filter={filter}
            onToggleSelect={(rowNumber) =>
              updateFaqs((rows) =>
                rows.map((r) => (r.rowNumber === rowNumber ? { ...r, selected: !r.selected } : r)),
              )
            }
            onToggleSelectAll={() => {
              const selectable = faqs.filter((r) => r.status !== 'error');
              const allSel = selectable.every((r) => r.selected);
              updateFaqs((rows) =>
                rows.map((r) => (r.status !== 'error' ? { ...r, selected: !allSel } : r)),
              );
            }}
            onDuplicateActionChange={(rowNumber, action: DuplicateAction) =>
              updateFaqs((rows) =>
                rows.map((r) =>
                  r.rowNumber === rowNumber ? { ...r, duplicateAction: action } : r,
                ),
              )
            }
          />
        )}
        {activeTab === 'symptom' && symptoms.length > 0 && (
          <SymptomPreviewTable
            rows={symptoms}
            filter={filter}
            onToggleSelect={(rowNumber) =>
              updateSymptoms((rows) =>
                rows.map((r) => (r.rowNumber === rowNumber ? { ...r, selected: !r.selected } : r)),
              )
            }
            onToggleSelectAll={() => {
              const selectable = symptoms.filter((r) => r.status !== 'error');
              const allSel = selectable.every((r) => r.selected);
              updateSymptoms((rows) =>
                rows.map((r) => (r.status !== 'error' ? { ...r, selected: !allSel } : r)),
              );
            }}
            onDuplicateActionChange={(rowNumber, action: DuplicateAction) =>
              updateSymptoms((rows) =>
                rows.map((r) =>
                  r.rowNumber === rowNumber ? { ...r, duplicateAction: action } : r,
                ),
              )
            }
          />
        )}
      </div>

      {uploadError && (
        <div className="mt-4 rounded-lg bg-red-50 p-3 text-sm text-red-700">{uploadError}</div>
      )}

      <div className="mt-6 flex items-center justify-between border-t border-gray-200 pt-4">
        <button
          onClick={onBack}
          className="rounded-lg border border-gray-300 px-5 py-2 text-sm hover:bg-gray-50"
        >
          アップロードに戻る
        </button>
        <div className="flex items-center gap-3">
          <span className="text-sm text-gray-500">
            登録対象: <span className="font-semibold">{selectedCount}件</span>
          </span>
          <button
            onClick={onSubmit}
            disabled={selectedCount === 0 || isUploading}
            className="rounded-lg bg-blue-600 px-6 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-50"
          >
            {isUploading ? '登録中...' : `${selectedCount}件を登録する`}
          </button>
        </div>
      </div>
    </div>
  );
};
